import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical, FloppyDisk, Eye, ArrowsClockwise, Lightning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsOf = (name) =>
  (name || "?").split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase();

/**
 * Single sortable row.
 * Receives the full player record + an editable baseline_elo + tier override.
 * Clicking the drag handle (dots icon) starts the reorder.
 */
function SortableRow({ player, position, onBaselineChange, onTierChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: player.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 px-3 py-2 rounded-md bg-background/40 border border-border/50 hover:border-primary/30 transition-colors"
      data-testid={`elo-order-row-${player.id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="text-muted-foreground hover:text-foreground touch-none cursor-grab active:cursor-grabbing"
        aria-label={`Drag ${player.name}`}
        data-testid={`elo-order-drag-${player.id}`}
      >
        <DotsSixVertical className="w-5 h-5" weight="bold" />
      </button>
      <div className="w-7 text-xs font-bold tabular-nums text-muted-foreground text-right">
        {position}
      </div>
      <Avatar className="h-8 w-8 border border-border shrink-0">
        <AvatarImage src={player.photo_url} alt={player.name} />
        <AvatarFallback className="text-[10px]">{initialsOf(player.name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{player.name}</p>
        <p className="text-[10px] text-muted-foreground tabular-nums">
          current ELO {Math.round(player.elo_rating || 0)}
          {player.baseline_elo != null && ` · base ${Math.round(player.baseline_elo)}`}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <select
          value={player._newTier || player.tier || "challenger"}
          onChange={(e) => onTierChange(player.id, e.target.value)}
          className="h-8 text-xs bg-background border border-border rounded px-2"
          data-testid={`elo-order-tier-${player.id}`}
        >
          <option value="premier">Premier</option>
          <option value="challenger">Challenger</option>
        </select>
        <Input
          type="number"
          step="50"
          min="500"
          max="3000"
          value={player._newBaseline ?? ""}
          onChange={(e) => onBaselineChange(player.id, e.target.value)}
          placeholder="baseline"
          className="h-8 w-24 text-xs tabular-nums"
          data-testid={`elo-order-baseline-${player.id}`}
        />
      </div>
    </div>
  );
}

/**
 * Drag-and-drop ELO reorder editor, embedded in the Admin Players tab.
 *
 * Workflow:
 *   1. Drag rows up/down to set the desired ELO order
 *   2. Type a baseline ELO into each row's input (or use "Auto-fill from order")
 *   3. Set tier per row (Premier / Challenger) — defaults to current tier
 *   4. Click "Preview" → calls /admin/repair-corrupted-elos with dry_run=true
 *      and scope='all' so the admin sees what will change across every
 *      tournament (past + ongoing + future)
 *   5. Click "Apply" → repairs everything for real, snapshots before for rollback
 *
 * Why this lives on the Players tab (option 6c):
 *   Admins manage players here — the ELO order belongs in the same workflow.
 *
 * Why scope='all':
 *   Past tournaments are replayed too so historical ELO movement reconciles
 *   with the new starting baselines. Future tournaments will simply use the
 *   updated `elo_rating` as their starting point (existing behaviour).
 */
export default function EloOrderEditor({ players, fetchData }) {
  // Filter to internal players (external/guest players don't get baselines)
  const initial = useMemo(
    () =>
      players
        .filter((p) => !p.is_external)
        .sort((a, b) => (b.elo_rating || 0) - (a.elo_rating || 0))
        .map((p) => ({
          ...p,
          _newBaseline: p.baseline_elo != null ? String(Math.round(p.baseline_elo)) : "",
          _newTier: p.tier,
        })),
    [players]
  );

  const [rows, setRows] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Re-sync when parent reloads players
  useEffect(() => { setRows(initial); }, [initial]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((current) => {
      const oldIndex = current.findIndex((r) => r.id === active.id);
      const newIndex = current.findIndex((r) => r.id === over.id);
      return arrayMove(current, oldIndex, newIndex);
    });
    setPreview(null);
  };

  const setBaseline = (id, value) => {
    setRows((cur) => cur.map((r) => (r.id === id ? { ...r, _newBaseline: value } : r)));
    setPreview(null);
  };
  const setTier = (id, value) => {
    setRows((cur) => cur.map((r) => (r.id === id ? { ...r, _newTier: value } : r)));
    setPreview(null);
  };

  /** Linear stepdown helper: top of list keeps its current value (or 2500 if blank),
   *  every lower row drops by `step`. Lets admins skip typing every number. */
  const autoFillFromOrder = () => {
    const top = parseInt(rows[0]?._newBaseline || "2500", 10) || 2500;
    const step = 100;
    setRows((cur) =>
      cur.map((r, i) => ({
        ...r,
        _newBaseline: String(Math.max(1000, top - i * step)),
      }))
    );
    setPreview(null);
    toast.success(`Auto-filled baselines starting at ${top}, −${step} per rank, floor 1000`);
  };

  /**
   * Tier ladder preset — applies the club's official baseline ladder to the
   * current order. Reads the ladder dynamically from `/api/admin/tier-ladder`
   * (single source of truth, edited in Admin → Settings → Tier Ladder Settings).
   * Tier auto-set per the rung's tier; rows below the last rung get the
   * configured fallback (default: 1000 · challenger).
   */
  const applyTierLadder = async () => {
    let rungs, fallback;
    try {
      const r = await axios.get(`${API}/admin/tier-ladder`, { withCredentials: true });
      rungs = r.data?.rungs || [];
      fallback = r.data?.fallback || { baseline_elo: 1000, tier: "challenger" };
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load tier ladder");
      return;
    }
    const ladderFor = (i) => {
      const oneIndexed = i + 1;
      const rung = rungs.find((r) => r.from_rank <= oneIndexed && oneIndexed <= r.to_rank);
      if (rung) return [rung.baseline_elo, rung.tier];
      return [fallback.baseline_elo, fallback.tier];
    };
    setRows((cur) =>
      cur.map((r, i) => {
        const [base, tier] = ladderFor(i);
        return { ...r, _newBaseline: String(base), _newTier: tier };
      })
    );
    setPreview(null);
    const summary = rungs.map((r) => r.baseline_elo).join(" / ");
    toast.success(`Tier ladder applied: ${summary} · rest ${fallback.baseline_elo}`);
  };

  const buildPayload = (dryRun) => ({
    baselines: rows
      .filter((r) => r._newBaseline !== "" && !isNaN(parseFloat(r._newBaseline)))
      .map((r) => ({
        player_id: r.id,
        baseline_elo: parseFloat(r._newBaseline),
        tier: r._newTier,
      })),
    scope: "all", // replay every tournament — past + current + future
    dry_run: dryRun,
  });

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/repair-corrupted-elos`,
        buildPayload(true),
        { withCredentials: true }
      );
      setPreview(r.data);
      const warnings = r.data.warnings || [];
      if (warnings.length > 0) {
        warnings.forEach((w) => toast.warning(w, { duration: 6000 }));
      }
      toast.success(
        `Preview ready — ${r.data.players?.length || 0} players, ${r.data.matches_to_replay} match${r.data.matches_to_replay === 1 ? "" : "es"} to replay across ${r.data.tournaments?.length || 0} tournament${r.data.tournaments?.length === 1 ? "" : "s"}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!preview) return;
    if (!window.confirm(
      `Apply new ELO order to ${preview.players?.length || 0} players?\n\n` +
      `• Every tournament (past, current, future) will be replayed with the new baselines\n` +
      `• ${preview.matches_to_replay} match${preview.matches_to_replay === 1 ? "" : "es"} will be recomputed\n` +
      `• A snapshot is saved automatically — rollback via "Rebaseline Premier ELO → Rollback latest"\n\n` +
      `This is irreversible without the rollback. Continue?`
    )) return;
    const pw = await promptSuperPassword({
      title: "Apply ELO order?",
      description: "Replays every tournament from scratch with the new baselines.",
      actionLabel: "Apply ELO Order",
    });
    if (!pw) return;
    setApplying(true);
    try {
      const r = await axios.post(
        `${API}/admin/repair-corrupted-elos`,
        buildPayload(false),
        { headers: { "X-Super-Password": pw }, withCredentials: true }
      );
      toast.success(r.data.message);
      setPreview(null);
      fetchData?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const ids = useMemo(() => rows.map((r) => r.id), [rows]);

  return (
    <Card className="bg-card border-secondary/30" data-testid="elo-order-editor-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Lightning className="w-5 h-5 text-secondary" weight="fill" />
          ELO Order &amp; Baselines
        </CardTitle>
        <CardDescription className="text-xs leading-relaxed">
          Drag rows to reorder players, type a baseline ELO per row (or use Auto-fill),
          and set each player's tier. Hitting Apply replays every tournament — past,
          ongoing, and future — through the new starting values. A snapshot is saved
          beforehand so you can roll back if needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline" onClick={applyTierLadder}
            className="border-primary/40 text-primary hover:bg-primary/10 text-xs"
            data-testid="elo-order-tier-ladder-btn"
          >
            <Lightning className="w-3.5 h-3.5 mr-1" weight="fill" />
            Apply tier ladder
          </Button>
          <Button
            size="sm" variant="outline" onClick={autoFillFromOrder}
            className="border-border text-xs"
            data-testid="elo-order-autofill-btn"
          >
            <ArrowsClockwise className="w-3.5 h-3.5 mr-1" weight="bold" />
            Auto-fill (−100 per rank)
          </Button>
          <Button
            size="sm" variant="outline" onClick={runPreview} disabled={loading}
            className="border-secondary/40 text-secondary hover:bg-secondary/10 text-xs"
            data-testid="elo-order-preview-btn"
          >
            <Eye className="w-3.5 h-3.5 mr-1" />
            {loading ? "Computing…" : "Preview"}
          </Button>
          <Button
            size="sm" onClick={runApply} disabled={applying || !preview}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-xs"
            data-testid="elo-order-apply-btn"
          >
            <FloppyDisk className="w-3.5 h-3.5 mr-1" weight="fill" />
            {applying ? "Applying…" : "Apply"}
          </Button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-2">
              {rows.map((p, idx) => (
                <SortableRow
                  key={p.id}
                  player={p}
                  position={idx + 1}
                  onBaselineChange={setBaseline}
                  onTierChange={setTier}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {preview && (
          <div className="border border-border rounded-lg" data-testid="elo-order-preview-results">
            <div className="px-4 py-2 border-b border-border bg-muted/10 flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview — {preview.players?.length || 0} players · {preview.matches_to_replay} match
                {preview.matches_to_replay === 1 ? "" : "es"} to replay
              </span>
              {preview.tournaments?.length > 0 && (
                <span className="text-[11px] text-muted-foreground italic">
                  across: {preview.tournaments.map((t) => t.name).join(", ")}
                </span>
              )}
            </div>
            <div className="divide-y divide-border max-h-72 overflow-y-auto">
              {(preview.players || []).map((p) => {
                const tierChanged = p.tier_before !== p.tier_after;
                const baseChanged = p.baseline_before !== p.baseline_after;
                return (
                  <div key={p.player_id} className="flex items-center gap-3 px-4 py-1.5 text-xs">
                    <Badge
                      variant="outline"
                      className={`text-[10px] capitalize w-20 justify-center ${p.tier_after === "premier" ? "border-primary/30 text-primary" : "border-border"}`}
                    >
                      {p.tier_after}
                      {tierChanged && <span className="ml-1 text-secondary">*</span>}
                    </Badge>
                    <span className="flex-1 font-medium truncate">{p.name}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-20 text-right">
                      elo → {p.elo_after_reset}
                    </span>
                    <span className="tabular-nums w-20 text-right">
                      base {p.baseline_after}
                      {baseChanged && <span className="text-secondary ml-1">*</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
